import React, { useState } from 'react';
import { CheckCircle2, Circle, Save, Download, RotateCcw, X } from 'lucide-react';
import { InvestigationQuestionsType } from '../data/types';

interface InvestigationQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  questions: InvestigationQuestionsType;
}

const InvestigationQuestionsModal: React.FC<InvestigationQuestionsModalProps> = ({
  isOpen,
  onClose,
  title,
  questions,
}) => {
  const [responses, setResponses] = useState<Record<string, boolean>>({});
  const [investigationName, setInvestigationName] = useState('');

  const toggleQuestion = (id: string) => {
    setResponses(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSave = () => {
    const data = {
      investigationName,
      disciplineType: title,
      timestamp: new Date().toISOString(),
      responses,
      completionPercentage: calculateCompletion()
    };
    console.log('Investigation data:', data);
    alert(`${title} investigation checklist saved!`);
  };

  const handleDownload = () => {
    const report = generateReport();
    const element = document.createElement('a');
    const file = new Blob([report], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${title}_investigation_${investigationName || 'report'}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleReset = () => {
    if (window.confirm('Clear all responses? This cannot be undone.')) {
      setResponses({});
      setInvestigationName('');
    }
  };

  const calculateCompletion = () => {
    const total = Object.values(questions).reduce((sum, cat) => sum + cat.questions.length, 0);
    const completed = Object.values(responses).filter(Boolean).length;
    return Math.round((completed / total) * 100);
  };

  const generateReport = () => {
    let report = `${title} INVESTIGATION CHECKLIST REPORT\n`;
    report += `Investigation: ${investigationName || 'Untitled'}\n`;
    report += `Generated: ${new Date().toLocaleString()}\n`;
    report += `Completion: ${calculateCompletion()}%\n`;
    report += `\n${'='.repeat(60)}\n\n`;

    Object.entries(questions).forEach(([key, category]) => {
      const categoryResponses = category.questions.map(q => responses[q.id]);
      const categoryCompletion = Math.round((categoryResponses.filter(Boolean).length / category.questions.length) * 100);
      
      report += `${category.title} [${categoryCompletion}%]\n`;
      report += `${'-'.repeat(40)}\n`;
      
      category.questions.forEach(question => {
        const isChecked = responses[question.id];
        report += `[${isChecked ? 'X' : ' '}] ${question.text}\n`;
      });
      report += `\n`;
    });

    return report;
  };

  const completion = calculateCompletion();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">{title} Investigation Checklist</h2>
            <p className="text-orange-100 text-sm mt-1">Comprehensive pivot points for thorough investigations</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-orange-600 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Investigation Name Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Investigation Name</label>
            <input
              type="text"
              value={investigationName}
              onChange={(e) => setInvestigationName(e.target.value)}
              placeholder="e.g., Subject Name - Date"
              className="w-full border border-gray-300 dark:border-slate-700 rounded px-4 py-2 text-gray-900 dark:text-slate-100 placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
            />
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">Overall Progress</h3>
              <span className="text-xl font-bold text-orange-500">{completion}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-orange-500 to-amber-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-4">
            {Object.entries(questions).map(([key, category]) => {
              const categoryResponses = category.questions.map(q => responses[q.id]);
              const categoryCompletion = Math.round((categoryResponses.filter(Boolean).length / category.questions.length) * 100);
              
              return (
                <div key={key} className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <div className="bg-gray-50 dark:bg-slate-800 px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-slate-900 dark:text-slate-100">{category.title}</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-slate-400">{categoryCompletion}%</span>
                        <div className="w-24 bg-gray-300 rounded-full h-2">
                          <div
                            className="bg-orange-500 h-2 rounded-full transition-all"
                            style={{ width: `${categoryCompletion}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Questions */}
                  <div className="p-4 space-y-2">
                    {category.questions.map((question) => (
                      <button
                        key={question.id}
                        onClick={() => toggleQuestion(question.id)}
                        className="w-full flex items-start gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left group"
                      >
                        {responses[question.id] ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-400 dark:text-slate-500 flex-shrink-0 mt-0.5 group-hover:text-gray-500" />
                        )}
                        <span className={`text-sm ${responses[question.id] ? 'text-gray-500 dark:text-slate-400 line-through' : 'text-gray-700 dark:text-slate-300'}`}>
                          {question.text}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-6 flex gap-3 justify-end">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 font-medium rounded-lg transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-lg transition-all"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-medium rounded-lg transition-all"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestigationQuestionsModal;